import { Box, Divider } from '@eatclub-apps/ec-component-library';
import { Collapse, useTheme } from '@material-ui/core';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ReactComponent as IconCalPrev } from '../../../../../assets/icons/caret_left.svg';
import { ReactComponent as IconCalNext } from '../../../../../assets/icons/caret_right.svg';
import { Button } from '../../../../../components/Button';
import { ConditionalWrapper } from '../../../../../components/ConditionalWrapper';
import { IconButton } from '../../../../../components/IconButton';
import { Spacer } from '../../../../../components/Spacer';
import { Typography } from '../../../../../components/Typography';
import { updateBookingAction } from '../../../../../data/actions/bookingAction';
import { fetchMonthlyAvailabilitiesAction } from '../../../../../data/actions/monthlyAvailabilitiesAction';
import { bookingKeys, bookingPropTypes } from '../../../../../data/models/Booking';
import { eventPropTypes } from '../../../../../data/models/Event';
import { monthlyAvailabilitiesPropTypes } from '../../../../../data/models/MonthlyAvailabilities';
import {
  isRestaurantClosedForDate,
  restaurantPropTypes,
} from '../../../../../data/models/Restaurant';
import { getHexColourWithOpacity } from '../../../../../theme';
import { trackEvent } from '../../../../../utils/analytics';
import { daysOfWeekShort } from '../../../../../utils/constants';
import {
  formatDateToMoment,
  formatMomentToDate,
  isEmpty,
  isNil,
} from '../../../../../utils/helpers';
import { useIsMobile, useIsSmallDevice } from '../../../../../utils/hooks';
import { getContrastTextColor } from '../../../../../utils/themeHelpers';
import useStyles from './CalendarStyles';
import DateCell from './DateCell';

const Calendar = ({
  booking,
  defaultView,
  onChange,
  restaurant,
  updateBooking,
  alwaysOpen,
  fetchMonthlyAvailabilities,
  monthlyAvailabilities,
  event,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(
    // eslint-disable-next-line no-nested-ternary
    alwaysOpen ? true : defaultView ? isNil(booking.date) : true,
  );
  const startOfToday = moment().startOf('day');
  const [viewedDate, setViewedDate] = useState(
    booking.date ? moment(booking.date).startOf('day') : startOfToday,
  );
  const isMobile = useIsMobile();
  const isSmallDevice = useIsSmallDevice();

  useEffect(() => {
    if (!isExpanded) {
      setViewedDate(booking.date ? moment(booking.date).startOf('day') : startOfToday);
    }
  }, [isExpanded]);

  useEffect(() => {
    setViewedDate(booking.date ? moment(booking.date).startOf('day') : startOfToday);
  }, [booking.date]);

  // Open it if alwaysOpen is changed to true
  useEffect(() => {
    if (alwaysOpen) {
      setIsExpanded(true);
    }
  }, [alwaysOpen]);

  // Refetch availability dots sometimes
  const viewedMonth = formatMomentToDate(viewedDate, 'YYYY-MM');
  useEffect(() => {
    if (!event?.data) {
      // TODO need to only fetch if the venue has some specific setting for this
      fetchMonthlyAvailabilities(viewedMonth);
    }
  }, [viewedMonth]);

  const onDateChange = (date) => {
    trackEvent('Booking', 'Updating Date', 'Booking Calendar');
    updateBooking(bookingKeys.date, formatMomentToDate(moment(date)));
    updateBooking(bookingKeys.selectedExperiences, []);

    setViewedDate(moment(date));

    if (!alwaysOpen && defaultView) {
      setIsExpanded(false);
    }

    onChange();
  };

  const sessions = useMemo(
    () => restaurant.data.areas.flatMap((area) => area.sessions),
    [restaurant.data.areas],
  );

  const getNumberBackgroundColor = (isDisabled, isSelected) => {
    if (isDisabled) {
      return theme.colors.darkMode ? '#ffffff11' : theme.colors.whiteSmoke;
    }

    if (isSelected) {
      const colorToUse =
        restaurant.data.experienceActiveBackgroundColor ?? // TODO this should be its own property
        restaurant.data.availabilityFieldsActiveStateColour;

      return getHexColourWithOpacity(colorToUse, restaurant.data.availabilityFieldsColourOpacity);
    }
    return '';
  };

  const getTileBgColor = (isDisabled) => {
    if (isDisabled) {
      return theme.colors.darkMode ? '#ffffff11' : theme.colors.whiteSmoke;
    }
    return '';
  };

  const getHeading = () => {
    if (isExpanded) {
      // Only show year if it's a different year (e.g. january of next year)
      const viewedDateYear = viewedDate.format('YYYY');

      if (viewedDateYear === moment().format('YYYY')) {
        return viewedDate.format('MMMM');
      }

      return viewedDate.format(isSmallDevice ? 'MMM YYYY' : 'MMMM YYYY');
    }

    const selectedDate = formatDateToMoment(booking.date);

    if (selectedDate.isSame(startOfToday, 'd')) {
      return 'Today';
    }

    if (selectedDate.isSame(moment(startOfToday).add(1, 'd'), 'd')) {
      return 'Tomorrow';
    }

    return selectedDate.format(isSmallDevice ? 'ddd Do MMM' : 'dddd Do MMM YYYY');
  };

  // Memoize available dates and do calculation for current month only
  const availableDates = useMemo(() => {
    const eventDates = event?.data?.eventDates;
    if (eventDates) return eventDates;

    const startOfMonth = moment(viewedDate).startOf('month');
    const endOfMonth = moment(viewedDate).endOf('month');
    const daysInMonth = endOfMonth.diff(startOfMonth, 'days') + 1;

    // Only check dates in current month
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = moment(startOfMonth).add(i, 'd');
      // Don't include past dates
      if (date.isBefore(startOfToday, 'day')) {
        return null;
      }
      // Don't include dates beyond daysInAdvance
      if (date.diff(startOfToday, 'days') >= restaurant.data.daysInAdvance) {
        return null;
      }
      return date;
    }).filter(
      (date) =>
        date &&
        !isRestaurantClosedForDate(date, sessions, restaurant.data.closures, restaurant.data.areas),
    );
  }, [
    event?.data?.eventDates,
    viewedDate,
    startOfToday,
    restaurant.data.daysInAdvance,
    restaurant.data.closures,
    restaurant.data.areas,
    sessions,
  ]);

  const index = availableDates.findIndex((availableDate) =>
    moment(booking.date).isSame(availableDate, 'd'),
  );

  const prevAvailableDate = index > 0 ? availableDates[index - 1] : null;
  const nextAvailableDate =
    index > -1 && index < availableDates.length - 1 ? availableDates[index + 1] : null;

  const bufferLength = (7 + moment(viewedDate).startOf('M').day() - 1) % 7;

  return (
    <Box>
      <Box>
        <Spacer
          style={{
            border: defaultView ? `1px solid ${theme.colors.outlineColour}` : '',
            borderRadius: '6px',
            height: '32px',
            padding: '8px 12px',
            width: 'calc(100% - 24px)',
          }}
        >
          <IconButton
            // variant='outlined'
            style={{ height: '28px', width: '28px' }}
            disabled={!isExpanded && isNil(prevAvailableDate)}
            onClick={() => {
              if (isExpanded) {
                setViewedDate(moment(viewedDate).subtract(1, 'M'));
              } else {
                onDateChange(prevAvailableDate);
              }
            }}
          >
            <IconCalPrev width={8} />
          </IconButton>

          <Box style={{ margin: 'auto' }}>
            <ConditionalWrapper
              condition={!isNil(booking.date) && defaultView && !alwaysOpen}
              wrapper={(children) => (
                <Button
                  variant='text'
                  onClick={() => setIsExpanded(!isExpanded)}
                  active={isExpanded}
                  showActiveBorder={false}
                  showArrowDown
                >
                  {children}
                </Button>
              )}
            >
              <Typography
                variant='h4'
                style={{ fontWeight: theme.fontWeights.medium, fontSize: '16px' }}
              >
                {getHeading()}
              </Typography>
            </ConditionalWrapper>
          </Box>

          <IconButton
            style={{ height: '28px', width: '28px' }}
            disabled={!isExpanded && isNil(nextAvailableDate)}
            onClick={() => {
              if (isExpanded) {
                setViewedDate(moment(viewedDate).add(1, 'M'));
              } else {
                onDateChange(nextAvailableDate);
              }
            }}
          >
            <IconCalNext width={8} />
          </IconButton>
        </Spacer>

        {/* {!defaultView && <Divider color={theme.colors.outlineColour} />} */}
      </Box>

      <Collapse in={isExpanded}>
        <Spacer
          direction='vertical'
          gap='m'
          style={{ padding: defaultView && isMobile ? '16px 0' : '16px' }}
        >
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(7, ${100 / 7}%)`,
            }}
          >
            {daysOfWeekShort.map((day) => (
              <Typography
                key={day}
                style={{ fontWeight: theme.fontWeights.medium, fontSize: '15px' }}
                centered
              >
                {day}
              </Typography>
            ))}
          </Box>
          <Box>
            <Box
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(7, 1fr)`,
                gridTemplateRows: `repeat(5, 1fr)`,
                gridGap: '2px',
              }}
            >
              {/* Buffer */}
              {Array.from({ length: bufferLength }).map((_, i) => (
                // eslint-disable-next-line
                <Box key={i} />
              ))}

              {Array.from({ length: moment(viewedDate).daysInMonth() }, (_, i) => {
                const startOfMonth = moment(viewedDate).clone().startOf('month');
                return startOfMonth.clone().add(i, 'd');
              }).map((date) => {
                const isDisabled = isNil(
                  availableDates.find((availableDate) => date.isSame(availableDate, 'd')),
                );

                const dateAsString = formatMomentToDate(date);

                // Are there no available experiences for this day?
                const experiencesFull =
                  !isDisabled &&
                  !isEmpty(monthlyAvailabilities.data?.allAvailableDates) &&
                  !(monthlyAvailabilities?.data?.allAvailableDates ?? [])?.includes(dateAsString);

                const isSelected = dateAsString === booking.date;
                const isToday = date.isSame(startOfToday, 'd');

                return (
                  <DateCell
                    key={dateAsString}
                    date={date}
                    isDisabled={isDisabled}
                    experiencesFull={experiencesFull}
                    isSelected={isSelected}
                    isToday={isToday}
                    isMobile={isMobile}
                    onDateChange={onDateChange}
                    getTileBgColor={getTileBgColor}
                    getNumberBackgroundColor={getNumberBackgroundColor}
                  />
                );
              })}
            </Box>
          </Box>
        </Spacer>

        <Divider color={theme.colors.dividerColor} />
      </Collapse>
    </Box>
  );
};

Calendar.defaultProps = {
  defaultView: false,
  onChange: () => {},
};

Calendar.propTypes = {
  booking: bookingPropTypes.isRequired,
  defaultView: PropTypes.bool,
  onChange: PropTypes.func,
  restaurant: restaurantPropTypes.isRequired,
  updateBooking: PropTypes.func.isRequired,
  alwaysOpen: PropTypes.bool.isRequired,
  fetchMonthlyAvailabilities: PropTypes.func.isRequired,
  monthlyAvailabilities: monthlyAvailabilitiesPropTypes.isRequired,
  event: eventPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  restaurant: state.restaurant,
  monthlyAvailabilities: state.monthlyAvailabilities,
  event: state.event,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateBooking: updateBookingAction,
      fetchMonthlyAvailabilities: fetchMonthlyAvailabilitiesAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Calendar);
